import { Component, type ErrorInfo, type ReactNode } from "react";
import { SectionCard } from "~/components/section-card";
import { StatusAlert } from "~/components/status-alert";
import { Button } from "~/components/ui/button";
import { captureAppException } from "~/lib/sentry";
import { m } from "~/lib/i18n";

interface RootErrorBoundaryProps {
  children: ReactNode;
}

interface RootErrorBoundaryState {
  error: Error | null;
}

export class RootErrorBoundary extends Component<
  RootErrorBoundaryProps,
  RootErrorBoundaryState
> {
  state: RootErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): RootErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    captureAppException(error, {
      extra: {
        componentStack: errorInfo.componentStack,
      },
      name: "root_provider_bootstrap_error",
    });
  }

  render() {
    if (this.state.error) {
      return (
        <main className="mx-auto mt-10 w-full max-w-2xl px-4">
          <SectionCard
            description={m.components_root_error_boundary_description()}
            title={m.components_root_error_boundary_title()}
          >
            <StatusAlert
              description={m.components_root_error_boundary_alert_description()}
              title={this.state.error.message}
              tone="destructive"
            />
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={() => window.location.reload()}
                type="button"
                variant="outline"
              >
                {m.components_root_error_boundary_reload_label()}
              </Button>
            </div>
          </SectionCard>
        </main>
      );
    }

    return this.props.children;
  }
}
